/**
 * profileStrength.js — ONE source of truth for seller/user profile strength.
 *
 * Used by AdDetails.js, SellerProfile.js, and any future profile-card surface.
 * Every page that displays "Profile strength %" MUST call computeProfileStrength
 * instead of rolling its own math — otherwise the same user shows different
 * values on different pages.
 *
 * Scoring (total = 100):
 *   profile photo          20   (avatar)
 *   full name              10   (name)
 *   verified email/account 15   (is_verified / verification.status === 'verified')
 *   phone                  15   (phone)
 *   location               10   (city || location)
 *   at least 1 active ad   10   (listing_count > 0 || active_listing_count > 0
 *                                || stats.published_ads_count > 0)
 *   bio / about            10   (bio)
 *   linked contact method  10   (whatsapp_phone || messenger_link)
 *
 * Input shape: pass whatever object you have (ad.seller, seller profile doc,
 * current user). The helper reads fields defensively so callers don't have to
 * normalize.
 */

export const PROFILE_STRENGTH_WEIGHTS = {
  avatar: 20,
  name: 10,
  verifiedEmail: 15,
  phone: 15,
  location: 10,
  hasActiveAd: 10,
  bio: 10,
  contactMethod: 10,
};

/**
 * Evaluate each scoring item against a user/seller object.
 * Returns an array so callers can render a checklist directly.
 */
export function getProfileStrengthItems(user) {
  const u = user || {};

  const isVerified =
    u.is_verified === true ||
    (u.verification && u.verification.status === 'verified');

  const hasLocation = Boolean(u.city || u.location);

  const activeAdCount =
    (typeof u.active_listing_count === 'number' && u.active_listing_count) ||
    (typeof u.listing_count === 'number' && u.listing_count) ||
    (u.stats && (u.stats.published_ads_count || u.stats.active_listing_count)) ||
    0;
  const hasActiveAd = activeAdCount > 0;

  const hasContactMethod = Boolean(u.whatsapp_phone || u.messenger_link);

  return [
    { key: 'avatar',         weight: PROFILE_STRENGTH_WEIGHTS.avatar,         passed: Boolean(u.avatar) },
    { key: 'name',           weight: PROFILE_STRENGTH_WEIGHTS.name,           passed: Boolean(u.name) },
    { key: 'verifiedEmail',  weight: PROFILE_STRENGTH_WEIGHTS.verifiedEmail,  passed: isVerified },
    { key: 'phone',          weight: PROFILE_STRENGTH_WEIGHTS.phone,          passed: Boolean(u.phone) },
    { key: 'location',       weight: PROFILE_STRENGTH_WEIGHTS.location,       passed: hasLocation },
    { key: 'hasActiveAd',    weight: PROFILE_STRENGTH_WEIGHTS.hasActiveAd,    passed: hasActiveAd },
    { key: 'bio',            weight: PROFILE_STRENGTH_WEIGHTS.bio,            passed: Boolean(u.bio) },
    { key: 'contactMethod',  weight: PROFILE_STRENGTH_WEIGHTS.contactMethod,  passed: hasContactMethod },
  ];
}

/**
 * Return the integer 0–100 profile-strength percentage for a user.
 */
export function computeProfileStrength(user) {
  const items = getProfileStrengthItems(user);
  const score = items.reduce((sum, it) => sum + (it.passed ? it.weight : 0), 0);
  return Math.max(0, Math.min(100, score));
}

/**
 * Localized labels for each scoring item — used by the checklist UI.
 */
export const PROFILE_STRENGTH_LABELS = {
  en: {
    avatar: 'Add a profile photo',
    name: 'Add your full name',
    verifiedEmail: 'Verify your account',
    phone: 'Add a phone number',
    location: 'Add your location',
    hasActiveAd: 'Post at least one ad',
    bio: 'Add a short bio',
    contactMethod: 'Link WhatsApp or Messenger',
  },
  ar: {
    avatar: 'أضف صورة الملف الشخصي',
    name: 'أضف اسمك الكامل',
    verifiedEmail: 'وثّق حسابك',
    phone: 'أضف رقم الهاتف',
    location: 'أضف موقعك',
    hasActiveAd: 'انشر إعلانًا واحدًا على الأقل',
    bio: 'أضف نبذة قصيرة',
    contactMethod: 'اربط واتساب أو ماسنجر',
  },
};

export function getProfileStrengthLabel(key, isArabic) {
  const pack = isArabic ? PROFILE_STRENGTH_LABELS.ar : PROFILE_STRENGTH_LABELS.en;
  return pack[key] || key;
}

export default computeProfileStrength;
